import { useEffect, useState } from "react";

import { getSupabase } from "./supabase";
import { useTenantId } from "./tenant";
import { monthBoundsFor, parseMonthToken } from "./calendarMonth.mjs";
import { divisionOrClause, ilikeEscape, isIlikeSafe } from "./divisionFilter.mjs";
import { excludesFeedIncomplete, feedIncompleteOrClause } from "./feedLag.mjs";
import { computeFallbackWindow } from "./fallbackWindow.mjs";
import { applyNowCutoffFilter } from "./nowCutoff.mjs";
import { periodWindowBounds } from "./periodWindow.mjs";
import { priorWindowBounds } from "./priorWindow.mjs";
import { foldDerivedRatio, foldSingleRow, type FoldResult } from "./ratioFolds.mjs";
import {
  isSnapshotLookupFresh,
  latestSnapshotPeriod,
  latestSnapshotSpec,
} from "./snapshotLatest.mjs";
import { drillSelect } from "./drillQuery.mjs";
import { scopeTenantTable } from "./tenantScope.mjs";
import { GATE_COPY, NO_SOURCE_DATA, UNCOMBINABLE_COPY } from "./sanityGate";
import type { ManifestWidget, WidgetFilterClause, WidgetSource } from "./manifest";
import { effectiveChartType, isKpiComparisonField } from "./manifest";

/**
 * Manifest sources still use the Zoho display names ("Meridian - KPI
 * Summary", "CQT-14") because the manifest drives both Zoho and the
 * self-host renderer during the dual-run window. Translate to the
 * snake_case Supabase identifiers here so the manifest stays canonical.
 */
const ZOHO_TABLE_TO_PG: Record<string, string> = {
  "Meridian - Service Events": "service_event",
  "Meridian - KPI Summary": "kpi_summary",
  "Meridian - Financial Health": "financial_health",
  "Meridian - Route KPIs": "route_kpis",
  "Meridian - AR Current": "ar_current",
  "Meridian - AR Transactions": "ar_transactions",
  "Meridian - Site Contracts": "site_contracts",
  "Meridian - Top AR Accounts": "top_ar_accounts",
  "Meridian - Contract Expirations": "contract_expirations",
  "Meridian - Audit Findings": "audit_finding",
  "Meridian - Insights": "insight",
  "Meridian - Customers": "customer",
  "Meridian - Assets": "asset",
  // MER-551: ticket-grain disposal. Its columns are the Navusoft export's own
  // names snake_cased (disposaldate, workordernumber, totalcost), plus three
  // we stamp: disposal_line_id, disposal_ticket_key and disposal_date.
  "Meridian - Disposal": "disposal",
};

export function resolveSourceName(source: WidgetSource): string {
  if (source.kind === "cqt") {
    const digits = source.name.replace(/^CQT-?/i, "");
    return `view_cqt_${digits.padStart(2, "0")}`;
  }
  if (source.kind === "table") {
    return ZOHO_TABLE_TO_PG[source.name] ?? source.name;
  }
  return source.name;
}

/**
 * Query layer for Phase 4 widget rendering.
 *
 * Each manifest widget declares its data shape via `source` + `field`.
 * The hook compiles that into a Supabase query, executes it, and
 * returns one of two shapes:
 *
 * - KPI scalar: `{ scalar: number | null }`
 * - Chart / table rows: `{ rows: Record<string, unknown>[] }`
 *
 * MER-245: every query is bound to the route slug's tenant_id via
 * scopeTenant(). RLS stays as defense in depth, but it is NOT the only
 * gate: a non-impersonating staff session matches all rows server-side,
 * so an unscoped query would blend tenants. tenantId is resolved by
 * useTenantId() and threaded into the executors below.
 */

export interface KpiResult {
  kind: "kpi";
  // MER-306 close: scalar widened to string for KPI tiles backed by text
  // categoricals (e.g. route_margin_trend_direction). The `latest`
  // aggregation passes the raw value through when display_format: text;
  // all numeric aggregations (sum / avg / count / min / max) keep
  // returning numbers, so callers can safely treat string as the
  // text-tile escape hatch.
  scalar: number | string | null;
  // MER-317: distinguishes "source wired but zero rows in the selected
  // range" from "query returned a valid scalar (including 0)". When
  // true, KPITile swaps the bare "n/a" string for the limited-activity
  // copy. False or undefined means the scalar speaks for itself: a
  // genuine zero (Adjustment Rate = 0% with rows present) still
  // formats as "0%", not "Limited activity".
  //
  // Per-aggregation rule:
  //   sum / avg / min / max -> isEmpty when the aggregate value is
  //     null or undefined. PostgREST returns a single result row even
  //     when no source rows match; the aggregate is null in that case.
  //   count / distinct_count -> isEmpty when the count is exactly 0.
  //   latest -> isEmpty when no row satisfied the order + filter (the
  //     query already excludes nulls in the target column).
  //   completion_pct (derived avg over is_completed) -> isEmpty when
  //     the underlying avg is null (no service events in range).
  isEmpty?: boolean;
  // MER-545: same aggregate evaluated over the window immediately before
  // the widget's own timeline window (priorWindow.mjs). Only populated
  // for widgets declaring `trend: vs_prior_period`; the renderer turns
  // (scalar, prior) into the KPITile "+X.X% vs prior" delta. Null when
  // the prior window has no data; absent when no trend was requested or
  // no honest prior window exists.
  prior?: number | null;
  // MER-715: the query resolved but the honest answer is to withhold the
  // number and say why (a stored ratio that cannot be combined across the
  // selected rows, a company figure whose rows disagree, a cost numerator that
  // is zero only because its feed is missing). The renderer shows this copy in
  // the gated tile slot, with no drill affordance, in place of the value.
  gateCopy?: string;
  // MER-467: the number IS shown, and this says what share of the population
  // it was built from. The renderer appends it to the tile caption. It never
  // replaces the value and never gates the tile; a reader who is told "based
  // on 84% of hauls" can judge the figure, a reader shown a blank tile cannot
  // judge anything and has lost a number they were using.
  coverageNote?: string;
}

export interface RowsResult {
  kind: "rows";
  rows: Record<string, unknown>[];
  // MER-470: set when the selected window had zero rows and the rows shown
  // are the latest POPULATED period instead. The renderer must surface this
  // label -- old data is never silently presented as current.
  fallbackPeriodLabel?: string;
  /** Wave 1 Disposal tab: set only for a widget declaring `row_limit`. How
   * many rows matched in all, so a capped list can say it is capped. */
  totalCount?: number;
}

export type WidgetData = KpiResult | RowsResult;

export interface QueryState {
  data: WidgetData | null;
  isLoading: boolean;
  error: Error | null;
}

// MER-636: exported so WidgetRenderer can resolve a range's width without a
// second copy of the table. priorWindow.mjs keeps its own mirror on purpose
// (see its header); this one is the single source for the render path.
export const TIMELINE_DAY_RANGES: Record<string, number> = {
  this_week: 7,
  last_week: 7,
  this_month: 31,
  last_month: 31,
  last_30_days: 30,
  last_90_days: 90,
  last_180_days: 180,
  ytd: 365,
  // Multi-month ranges used by chart widgets like L1.3 Revenue Trend.
  last_4_weeks: 28,
  last_4_months: 122,
  last_6_months: 183,
  last_12_months: 366,
  last_quarter: 92,
  last_year: 366,
};

// Supabase query builders are heavily typed; we operate on the
// post-`.select()` filter builder which exposes eq/neq/gt/in/is/not.
// Typing as `any` keeps the helpers concise without sacrificing
// behavior; the public-facing return types are still strongly typed.
// Exported so KpiComparisonTile (and any future per-pane runner) can
// reuse the same widget.filter + widget.timeline handling.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SupabaseFilter = any;

/**
 * Match meridian_pipeline.supabase.views.pg_column_name on the client.
 * The manifest still uses Zoho display casing ("Is_Payment", "Bill Amount")
 * during the dual-run window, but the Supabase loader normalizes column
 * names to lowercase snake_case. Without canonicalization, PostgREST
 * rejects filter/select keys with `column does not exist`.
 *
 * Rule: lowercase, replace runs of non-[a-z0-9_] with `_`, strip leading
 * and trailing underscores.
 */
export function canonicalColumnName(col: string): string {
  return col
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * MER-309: source-aware column-name resolver.
 *
 * Canonical Supabase tables (kpi_summary, service_event, financial_health,
 * etc.) carry snake_case columns, so the lowercasing rule in
 * `canonicalColumnName` matches the loader's behaviour.
 *
 * CQT views are different. `dashboard/supabase/migrations/0003_canonical_views.sql`
 * emits PascalCase aliases like "Row_Count" and "Total_Revenue" by design so
 * the Zoho-style report-builder consumers stay compatible during the dual-run
 * window. Those quoted aliases survive Postgres identifier folding, so PostgREST
 * needs the exact casing on the request URL. Lowercasing them yields
 * `column does not exist` and the Internal DQ tab renders n/a (L3d.1a, L3d.1b,
 * L3d.2a KPI path).
 *
 * Rule: when the source is a CQT view, pass the raw column through. Otherwise
 * fall back to the canonical rule.
 *
 * Scope note: applied to the column-lookup site in `executeKpiQuery` and to
 * `applyFilters`. Per the pre-flight open question, timeline columns stay on
 * `canonicalColumnName` because no in-scope CQT widget uses a timeline;
 * revisit if a future CQT widget ships one.
 */
export function columnNameFor(rawCol: string, source: WidgetSource | undefined): string {
  if (source?.kind === "cqt") return rawCol;
  return canonicalColumnName(rawCol);
}

export function applyFilters(query: SupabaseFilter, widget: ManifestWidget): SupabaseFilter {
  const filters = widget.filter ?? {};
  let next: SupabaseFilter = query;
  for (const [rawCol, raw] of Object.entries(filters)) {
    if (raw === undefined) continue;
    const col = columnNameFor(rawCol, widget.source);
    // MER-504: division values are manifest slugs (lowercase) while
    // warehouse rows store tenant-specific casing (DWS "Jackson", BD
    // "central_coast"). Match case-insensitively via ilike; charset
    // guard falls back to the case-sensitive path on unsafe values.
    const isDivision = col === "division";
    if (typeof raw === "object" && raw !== null && "op" in raw) {
      const clause = raw as WidgetFilterClause;
      if (isDivision && clause.op === "in") {
        const orBody = divisionOrClause(col, (clause.value as string[]) ?? []);
        if (orBody) {
          next = next.or(orBody);
          continue;
        }
      }
      if (isDivision && clause.op === "eq" && isIlikeSafe(clause.value)) {
        next = next.ilike(col, ilikeEscape(clause.value as string));
        continue;
      }
      switch (clause.op) {
        case "eq":
          next = next.eq(col, clause.value);
          break;
        case "neq":
          next = next.neq(col, clause.value);
          break;
        case "gt":
          next = next.gt(col, clause.value);
          break;
        case "gte":
          next = next.gte(col, clause.value);
          break;
        case "lt":
          next = next.lt(col, clause.value);
          break;
        // MER-589: `lte` was live on days_to_expiration for L6b.3a and L6b.5
        // (Sales Pipeline, both tenants) and silently dropped, so the
        // "expiring within N days" tiles counted EVERY contract.
        case "lte":
          next = next.lte(col, clause.value);
          break;
        case "in":
          next = next.in(col, (clause.value as unknown[]) ?? []);
          break;
        // MER-620: the complement of `in`, so two segment tiles partition one
        // population. PostgREST spells it `not.in.(a,b)` and supabase-js takes
        // the value list as a parenthesised, comma-joined string rather than an
        // array, so each member is quoted here. An empty list would mean "not
        // in nothing", which every row satisfies; that is the right reading
        // (no segment declared, so nothing is excluded) and it matches what
        // `in` does with an empty list from the other side.
        case "not_in": {
          const members = ((clause.value as unknown[]) ?? []).map(
            (v) => `"${String(v).replace(/"/g, '\\"')}"`,
          );
          next = next.not(col, "in", `(${members.join(",")})`);
          break;
        }
        case "isnull":
          next = next.is(col, null);
          break;
        // MER-589: the manifests spell this `is_not_null` (33 clauses on the
        // Safety tiles). Only `notnull` was handled, so the safety_score
        // not-null guard never applied.
        case "is_not_null":
        case "notnull":
          next = next.not(col, "is", null);
          break;
        case "latest":
          // Not expressible as a PostgREST predicate: it needs MAX(col) first.
          // `resolveLatestFilters` rewrites it to `eq` before the query builds.
          // Reaching here means a call site skipped that step.
          throw new Error(
            `filter op 'latest' on '${col}' reached applyFilters unresolved. ` +
              `Call resolveLatestFilters(widget, tenantId) first (MER-589).`,
          );
        default: {
          // MER-589: fail loud. The absent default branch is what let three
          // ops go missing for months while the queries ran unfiltered and the
          // tiles showed confident wrong numbers.
          const bad: never = clause.op;
          throw new Error(
            `Unsupported filter op '${String(bad)}' on column '${col}'` +
              (widget.id ? ` (widget ${widget.id})` : "") +
              `. Add a case here and to WidgetFilterOp, or fix the manifest.`,
          );
        }
      }
    } else if (isDivision && isIlikeSafe(raw)) {
      next = next.ilike(col, ilikeEscape(raw as string));
    } else {
      next = next.eq(col, raw);
    }
  }
  // MER-588: resolveLatestFilters pinned this snapshot widget to the tenant's
  // newest snapshot period. Applied here, after the declared filters, so every
  // aggregation (sum, avg, a DERIVED_RATIOS SUM/SUM) runs over one snapshot
  // instead of every snapshot ever retained. No period (no snapshot rows)
  // adds nothing: the snapshot marker filter alone already selects no rows.
  if (widget.snapshot_period && widget.source) {
    const spec = latestSnapshotSpec(
      resolveSourceName(widget.source),
      filters as Record<string, unknown>,
    );
    if (spec) next = next.eq(spec.period, widget.snapshot_period);
  }
  return next;
}

/**
 * MER-589: resolve `{op: "latest"}` clauses into concrete `{op: "eq"}` pins.
 *
 * `latest` means "the newest value this column holds for this tenant", which
 * PostgREST cannot express as a predicate (it needs a MAX subquery). So it is
 * resolved to a literal here, BEFORE the query builds, and `applyFilters` stays
 * synchronous.
 *
 * Why a global max rather than a per-row-key latest: the ASSET feed writes one
 * row per vehicle per import window, and a vehicle that leaves the fleet simply
 * stops appearing. Taking each vehicle's own latest row would keep a
 * decommissioned truck contributing its final safety score to the fleet average
 * for ever, and "Active Assets" would never decrement. Prod already carries
 * retired-unit markers (asset_ids whose vehicle_name gained an `OLD-` prefix),
 * so this is not hypothetical. Pinning to the newest import window answers the
 * honest question: what did the most recent run report?
 *
 * The trade-off is that a vehicle missing from the newest window drops out of
 * the denominator. That is the correct reading of "not reported this window",
 * and it is visible rather than silent: the count moves.
 */
const latestValueCache = new Map<string, Promise<unknown>>();
// MER-588: unlike the asset pin above, a snapshot period is REPLACED when the
// next month's refresh lands, so an entry must not outlive a refresh or an open
// tab keeps filtering to a period that is gone and every snapshot tile reads
// empty until reload. Entries expire (SNAPSHOT_PERIOD_TTL_MS).
const snapshotPeriodCache = new Map<
  string,
  { startedAt: number; pending: Promise<string | null> }
>();

/** Test seam: the caches are module-level, so a test run must be able to clear them. */
export function __resetLatestValueCache(): void {
  latestValueCache.clear();
  snapshotPeriodCache.clear();
}

async function latestValueFor(table: string, col: string, tenantId: string): Promise<unknown> {
  const key = `${tenantId}::${table}::${col}`;
  const hit = latestValueCache.get(key);
  if (hit) return hit;
  // One request per (tenant, table, column) even though eight Safety tiles ask
  // for the same pin on the same render.
  const pending = (async () => {
    const supabase = getSupabase();
    const q: SupabaseFilter = scopeTenantTable(supabase.from(table).select(col), tenantId, table);
    const { data, error } = await q.not(col, "is", null).order(col, { ascending: false }).limit(1);
    if (error) throw new Error((error as { message?: string }).message ?? "Supabase error");
    const rows = (data ?? []) as Record<string, unknown>[];
    return rows.length ? rows[0][col] : undefined;
  })();
  latestValueCache.set(key, pending);
  return pending;
}

/**
 * MER-588: the tenant's newest snapshot period for a widget that pins
 * `period_type: snapshot` on a snapshot source (snapshotLatest.mjs owns the
 * rule and its rationale). Attached as `snapshot_period`; applyFilters turns it
 * into the row restriction. One lookup per (tenant, table) shared by every
 * snapshot tile on the page for SNAPSHOT_PERIOD_TTL_MS, and never cached when
 * it fails.
 */
async function resolveLatestSnapshot(
  widget: ManifestWidget,
  tenantId: string,
): Promise<ManifestWidget> {
  if (!widget.source || widget.snapshot_period !== undefined) return widget;
  const table = resolveSourceName(widget.source);
  const spec = latestSnapshotSpec(table, widget.filter as Record<string, unknown> | undefined);
  if (!spec) return widget;
  const key = `${tenantId}::${table}::latest_snapshot`;
  const now = Date.now();
  const hit = snapshotPeriodCache.get(key);
  let pending = hit && isSnapshotLookupFresh(hit.startedAt, now) ? hit.pending : undefined;
  if (!pending) {
    const lookup = (async () => {
      const supabase = getSupabase();
      let q: SupabaseFilter = scopeTenantTable(
        supabase.from(table).select(spec.period),
        tenantId,
        table,
      );
      q = q
        .eq(spec.marker.column, spec.marker.value)
        .not(spec.period, "is", null)
        .order(spec.period, { ascending: false })
        .limit(1);
      // Same forward-dated-row cap the tile query applies, so the lookup can
      // never pick a period the tile query would then exclude.
      q = applyNowCutoffFilter(q, table, {});
      const { data, error } = await q;
      if (error) throw new Error((error as { message?: string }).message ?? "Supabase error");
      return latestSnapshotPeriod((data ?? []) as Record<string, unknown>[], spec);
    })();
    const entry = { startedAt: now, pending: lookup };
    snapshotPeriodCache.set(key, entry);
    lookup.catch(() => {
      if (snapshotPeriodCache.get(key) === entry) snapshotPeriodCache.delete(key);
    });
    pending = lookup;
  }
  return { ...widget, snapshot_period: await pending };
}

export async function resolveLatestFilters(
  widget: ManifestWidget,
  tenantId: string,
): Promise<ManifestWidget> {
  widget = await resolveLatestSnapshot(widget, tenantId);
  const filters = widget.filter ?? {};
  const pinned = Object.entries(filters).filter(
    ([, v]) => typeof v === "object" && v !== null && (v as WidgetFilterClause).op === "latest",
  );
  if (pinned.length === 0 || !widget.source) return widget;

  const table = resolveSourceName(widget.source);
  const next: Record<string, unknown> = { ...filters };
  for (const [rawCol] of pinned) {
    const col = columnNameFor(rawCol, widget.source);
    const value = await latestValueFor(table, col, tenantId);
    if (value === undefined || value === null) {
      // No non-null value anywhere in the column for this tenant. Best Disposal
      // has zero asset rows at all, so its Safety tiles must render the
      // awaiting-source-data gate rather than a 0 that reads as perfect safety.
      // Never fall through with the clause dropped: that would aggregate the
      // whole table, which is the defect this function exists to fix.
      throw new Error(
        `${NO_SOURCE_DATA}: ${table}.${col} has no values for this tenant, ` +
          `so '${rawCol}' cannot be pinned to its latest (MER-589).`,
      );
    }
    next[rawCol] = { op: "eq", value } as WidgetFilterClause;
  }
  return { ...widget, filter: next as ManifestWidget["filter"] };
}

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Inclusive-lower / exclusive-upper bounds for an entire calendar month,
 * formatted to match the column type.
 *
 * Date columns (kpi_summary.period_date etc.) need full ISO dates;
 * "2026-05" can't be cast to date and Postgres rejects the query with
 * `invalid input syntax for type date`. Text columns holding YYYY-MM
 * (financial_health.period_start) need bare YYYY-MM bounds; a YYYY-MM-DD
 * lower bound sorts lexicographically AFTER the bare YYYY-MM rows and
 * excludes them.
 *
 * The DWS schema convention is consistent: column names ending in `_date`
 * are typed `date`, everything else is text. Use that as the heuristic.
 */
function monthBounds(col: string, offset: number): { gte: string; lt: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + offset;
  const start = new Date(y, m, 1);
  const end = new Date(y, m + 1, 1);
  if (col.endsWith("_date")) {
    return { gte: isoDate(start), lt: isoDate(end) };
  }
  return { gte: monthKey(start), lt: monthKey(end) };
}

// MER-545: tables whose rows are period buckets anchored to the bucket
// start. A vs-prior trailing window over these compares a partial current
// bucket against complete prior buckets; useWidgetQuery refuses the prior
// pass for them (see the trend branch).
// MER-636: the same set is what applyTimeline hands to periodWindowBounds --
// these are exactly the tables where an anchor stands for a whole period, so
// a window has to be tested against the period and not against the anchor.
// MER-693: exported so WidgetRenderer can tell a period-bucketed source from an
// event-grain one without a second copy of the set. The feed-lag guard is
// refused for these: their vs-prior pass is already refused below and
// periodWindow.mjs already ends their trailing windows at the last COMPLETE
// bucket, so guarding them again would answer an answered question.
export const PERIOD_GRAIN_TABLES = new Set(["kpi_summary", "route_kpis", "financial_health"]);

/**
 * The effective period grain for a widget, or undefined when we cannot know
 * it. applyGlobalFilters has already resolved filter.period_type by the time a
 * widget reaches the query layer: a declared grain survives the default load,
 * an explicit Period selection overrides it, and MER-362 auto-applies the
 * global value to silent widgets on period-aware sources. A widget that
 * reaches here with no grain gets the pre-MER-636 bare cutoff rather than a
 * guess -- guessing monthly on weekly rows would be the same class of error
 * this fix removes.
 */
function effectivePeriodGrain(widget: ManifestWidget): string | undefined {
  const raw = widget.filter?.period_type;
  if (typeof raw === "string") return raw;
  if (raw && typeof raw === "object" && "value" in raw) {
    const v = (raw as WidgetFilterClause).value;
    if (typeof v === "string") return v;
  }
  return undefined;
}

/**
 * Apply the widget's timeline filter to a Supabase query.
 *
 * mode: snapshot     -> no filter (the column may even be null)
 * mode: period       -> exact-period match for this_month / last_month
 *                       using column-type-aware bounds (YYYY-MM for text
 *                       columns, YYYY-MM-DD for date columns).
 * period-grain table -> MER-636: bucket bounds from periodWindowBounds. Rows
 *                       on kpi_summary / route_kpis / financial_health are
 *                       whole periods stamped at their start, so the window
 *                       is tested against the PERIOD, not against the anchor.
 * everything else    -> trailing window: gte (today - N days). Used for
 *                       last_30_days, last_90_days, ytd, etc. on event-grain
 *                       sources, where each row is one dated event.
 *
 * Pre-MER-138 the timeline always took the trailing-window branch, even
 * for `mode: period this_month`. That broke text YYYY-MM columns because
 * a full-ISO-date cutoff (`2026-04-20`) sorted lexicographically after
 * the bare YYYY-MM rows (`2026-04`), excluding everything. The first
 * MER-138 attempt sent bare YYYY-MM bounds, which broke date columns
 * (`invalid input syntax for type date: "2026-05"`). This iteration
 * dispatches on the column name suffix.
 */
// MER-294: in demo mode (?demo=1), override every widget's effective
// range to YTD on the initial paint. DWS's QTRLYADV billing leaves MTD
// and Last 30 days mostly empty; prospects need to land on populated
// tiles. The override happens at the query layer (not the manifest)
// so it stays scoped to demo mode and doesn't shift operator
// defaults. Snapshot-mode widgets are unaffected (no timeline window
// applies).
//
// Read URL params here directly; this code runs in the browser so
// window.location is the source of truth. A React-hook-based path
// would need to thread demoMode through every caller, which is more
// surface for a render-layer override.
function isDemoMode(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    const v = params.get("demo");
    return v === "1" || v === "true";
  } catch {
    return false;
  }
}

export function applyTimeline(query: SupabaseFilter, widget: ManifestWidget): SupabaseFilter {
  if (!widget.timeline) return query;
  if (widget.kpi_subtype === "snapshot") return query;

  const tl = widget.timeline;
  if (tl.mode === "snapshot") return query;
  if (!tl.column) return query;
  const col = canonicalColumnName(tl.column);

  // MER-294: demo mode forces YTD. Skip the calendar-month branch so a
  // widget defaulting to this_month / last_month draws against the
  // YTD trailing window instead. The trailing-window branch below
  // handles ytd via TIMELINE_DAY_RANGES.
  const effectiveRange = isDemoMode() ? "ytd" : tl.range;

  // MER-692: an explicit calendar month (`?range=YYYY-MM` ->
  // `month:YYYY-MM`). It has no entry in TIMELINE_DAY_RANGES on purpose and
  // must never reach the trailing branch below, where a missing width means
  // NO window at all (every row the table holds). It resolves here, always
  // with both bounds:
  //   - period-bucketed tables go through periodWindowBounds, so a monthly
  //     row is in exactly when it is that month and a weekly row is in when
  //     the month mostly covers its week;
  //   - every other table, and a period table periodWindowBounds has no
  //     opinion on (a weekly grain on a TEXT month-key column), gets the
  //     literal calendar month on the timeline column, `_date` suffix
  //     deciding ISO date vs bare YYYY-MM. That is the same shape the
  //     this_month branch below emits for those columns today.
  // Demo mode still forces YTD: effectiveRange is "ytd" there, so no month.
  const month = parseMonthToken(effectiveRange);
  if (month) {
    if (widget.source && PERIOD_GRAIN_TABLES.has(resolveSourceName(widget.source))) {
      const bounds = periodWindowBounds({
        grain: effectivePeriodGrain(widget),
        range: effectiveRange,
        days: undefined,
        column: col,
        today: new Date(),
      });
      if (bounds) return query.gte(col, bounds.gte).lt(col, bounds.lt);
    }
    const calendar = monthBoundsFor(month, col);
    if (calendar) return query.gte(col, calendar.gte).lt(col, calendar.lt);
  }

  if (
    !isDemoMode() &&
    tl.mode === "period" &&
    (tl.range === "this_month" || tl.range === "last_month")
  ) {
    const offset = tl.range === "last_month" ? -1 : 0;
    const { gte, lt } = monthBounds(col, offset);
    return query.gte(col, gte).lt(col, lt);
  }

  const days = TIMELINE_DAY_RANGES[effectiveRange ?? ""];

  // MER-636 / MER-626 / MER-613: on the tables whose rows ARE periods, select
  // buckets by OVERLAP with the window instead of by anchor date. The anchor
  // is the bucket's first day, so a bare `gte(col, today - N)` asks whether
  // one day cleared the cutoff and answers for the whole month or week behind
  // it. On 2026-09-09 that dropped the 1 August bucket out of every 30-day
  // window (Cash Collected summed $0 against a real $1,439,028.36), dropped
  // 1 June out of a 90-day one (DWS revenue $2,147,231 over two complete
  // months plus a partial September, against $3,931,335 for the three
  // complete months asked for), and made a weekly "last 30 days" cover 35
  // days on some weekdays and 28 on others. periodWindowBounds owns the rule
  // and its rationale; it returns null when it has no opinion (unknown grain
  // or unknown range) and we keep the bare cutoff below.
  if (widget.source && PERIOD_GRAIN_TABLES.has(resolveSourceName(widget.source))) {
    const bounds = periodWindowBounds({
      grain: effectivePeriodGrain(widget),
      range: effectiveRange,
      days,
      column: col,
      today: new Date(),
    });
    if (bounds) return query.gte(col, bounds.gte).lt(col, bounds.lt);
  }

  // Trailing-window fallback. Pre-MER-138 behavior for any non-period
  // mode and for ranges that aren't a single calendar month. Event-grain
  // sources (service_event, ar_transactions, CQT views) stay here: their rows
  // are individual dated events, so the anchor IS the event and a bare cutoff
  // is the correct test.
  if (!days) return query;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return query.gte(col, isoDate(cutoff));
}

/**
 * MER-157 stopgap (B): exclude FUTURE-period rows on tables that emit
 * forward-dated rows from advance-billed invoices.
 *
 * MER-500: the cutoff filters the real DATE column `period_date` with an
 * inclusive end-of-current-month bound. The previous
 * `lte("period_start", "YYYY-MM")` text compare excluded every
 * current-month WEEKLY row ('2026-06-01' > '2026-06') on all NOW_CUTOFF
 * tables. Logic lives in nowCutoff.mjs so node:test covers it directly.
 *
 * Opt out per-widget via `respect_now_cutoff: false` once a forecast
 * widget legitimately needs forward data. Today no widget sets this.
 */
interface NowCutoffOpt {
  respect_now_cutoff?: boolean;
}

export function applyNowCutoff(
  query: SupabaseFilter,
  widget: ManifestWidget,
  table: string,
): SupabaseFilter {
  return applyNowCutoffFilter(query, table, {
    respectNowCutoff: (widget as ManifestWidget & NowCutoffOpt).respect_now_cutoff,
  });
}

/**
 * MER-693: drop the periods the pipeline itself marked FEED_INCOMPLETE from
 * reads that AVERAGE them.
 *
 * normalize.py stamps kpi_summary.data_quality = 'FEED_INCOMPLETE' on a
 * finished period whose invoice line count came in at or under a fraction of
 * its own trailing median (_invoice_starved_flag). Sums and counts keep those
 * rows: a short-reported week's dollars and events are real, and dropping them
 * would understate a total an owner reconciles. Averages and ratios cannot:
 * a mean weights a one-fifth-reported week like a full one, and a
 * SUM(num)/SUM(denom) takes a numerator from the starved feed against a
 * denominator that is not.
 *
 * The predicate is an or(), never a bare neq. In SQL every comparison against
 * NULL is unknown, so excluding by inequality alone drops every UNSTAMPED row
 * as well, which on a real tenant is the majority of them. That is what
 * blanked every good row in the MER-687 probe. feedLag.mjs owns both the shape
 * of the clause and the decision of when it applies.
 */
export function applyFeedQuality(
  query: SupabaseFilter,
  table: string,
  shape: string,
): SupabaseFilter {
  if (!excludesFeedIncomplete({ table, shape })) return query;
  return query.or(feedIncompleteOrClause());
}

const POSTGREST_AGGS = new Set(["sum", "avg", "count", "min", "max"]);

function toScalar(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

// MER-483: hoisted to module scope and exported so ChartWidget can trend
// derived-ratio columns (per-bucket SUM/SUM) with the same registry.
// MER-309: derived ratios that exist only as Zoho aggregate formulas
// (`src/meridian_pipeline/zoho/aggregate_formulas.py`). The Supabase
// schema stops at gross_margin_pct / disposal_fee_recovery_pct in
// `migrations/0008_kpi_summary_computed_columns.sql`; the three Internal
// DQ ratios below were never migrated to GENERATED STORED columns.
// Compute them at query time from the building-block columns that ARE
// canonical, mirroring the `completion_pct` precedent above. Returns a
// fraction in [0,1]; format.ts multiplies by 100 for the percent glyph
// (MER-184 convention).
//
// Open question 5 from the pre-flight (PostgREST multi-aggregate
// response shape): we send the select clause with explicit aliases
// (`select=num:colA.sum(),denom:colB.sum()`) so the response keys are
// deterministic regardless of PostgREST version. The codebase only
// previously requested single aggregates, so this is the first
// multi-aggregate caller and the explicit aliasing avoids any
// collapsed-`sum`-key ambiguity. Tested-by-construction; spot-check
// against the live droplet post-merge per MER-36 done criteria.
//
// MER-320: extend the same SUM(num) / SUM(denom) pattern to three
// ratio columns that DO exist as stored fractions but whose widgets
// used `aggregation: avg` and so produced AVG-of-ratios. AVG of
// per-row ratios is statistically biased: a low-volume period (e.g.
// $100 revenue with 50% disposal share) over-weights the result vs.
// a high-volume period ($1M revenue at 10%). The unbiased window
// aggregate is SUM(numerator) / SUM(denominator). The row-level
// stored columns (disposal_burden_pct on kpi_summary,
// fuel_pct_of_revenue / maintenance_pct_of_revenue on
// financial_health) are preserved as snapshot/debug values; widgets
// now route through this branch instead of avg-ing them. Affects
// L3a.1b Disposal Burden %, L3a.4f Fuel as % of Revenue, L3a.4g
// Maintenance as % of Revenue.
export const DERIVED_RATIOS: Record<string, { table: string; num: string; denom: string }> = {
  adjustment_rate_pct: {
    table: "kpi_summary",
    num: "adjustment_credits",
    denom: "billed_revenue",
  },
  disposal_posting_coverage_pct: {
    table: "service_event",
    num: "disposal_posted_count",
    denom: "stops_count",
  },
  miles_driven_coverage_pct: {
    table: "service_event",
    num: "miles_driven_count",
    denom: "stops_count",
  },
  // MER-825: the hook lift and tractor trailer swap rate. A haul is a roll
  // off haul (AJ, 2026-09-21), so these lines count pulls and their swap
  // share divides by their own pulls, never by hauls. Both sides are stored
  // kpi_summary columns on the SAME row, and the row is one
  // (division, equipment_type, period), so the tile's equipment_type filter
  // scopes both halves at once: a hook lift swap can never be divided by a
  // tractor trailer pull. The numerator is inside the denominator by
  // construction, because swap is one of the three service types pull_count
  // counts, which is the subset property MER-630 found broken on the haul
  // tile and this shape cannot break.
  swap_share_of_pulls: {
    table: "kpi_summary",
    num: "swap_count",
    denom: "pull_count",
  },
  // MER-320: SUM/SUM replacements for AVG-of-ratios on the three
  // cost-burden tiles.
  disposal_burden_pct: {
    table: "kpi_summary",
    num: "total_disposal_cost",
    denom: "total_revenue",
  },
  // MER-421: $/ton must be SUM(per-LOB cost) / SUM(per-LOB tons), not an
  // avg of per-row ratios. The per-row disposal_cost_per_ton denominator is
  // total_disposal_tons (DISPOSAL ticket Quantity, UOM-normalized), so FL/HL/TT
  // contribute real tons instead of NULL (which collapsed the avg to RO-only).
  // MER-589: the numerator is the TON-MATCHED cost, not all disposal cost.
  // Six Delta facilities bill by the cubic yard while carrying Uom='ton' (a
  // 30-yard pull at $8). That spend is real and stays in total_disposal_cost,
  // but it produces no tonnage, so including it here halved every $/ton on the
  // dashboard -- $20 where the truth is $48. Equal to total_disposal_cost for a
  // hauler whose facilities all bill by weight, which is Best Disposal today.
  // MER-719: the denominator is the PRICED tonnage, the mirror of the
  // ton-matched numerator. total_disposal_tons also carries weighed-but-
  // unpriced loads (a truck crossed the scale, nobody had priced the ticket),
  // which are real tons that belong in Total Tons but funded none of this
  // cost. Dividing priced cost by all tons would deflate the rate by exactly
  // the share of unpriced tonnage.
  disposal_cost_per_ton: {
    table: "kpi_summary",
    num: "disposal_cost_ton_matched",
    denom: "disposal_tons_ton_matched",
  },
  // MER-646: the two operator rates Seth Heath flagged, read as one division
  // over the selected rows instead of an unweighted average of stored weekly
  // rates. A 3-stop central_coast roll-off week used to count as much as a
  // 406-stop main front-load week: Best Disposal's Stops per Route per Day
  // read 14.12 that way against 11.86 on the sum basis over its August 2026
  // weekly rows (roll-off alone reads 6.17, which is the ~7 Seth expected of
  // roll-off), and its Lifts / Labor Hour read a 36.23 average of ten weekly
  // rates. Components land on migration 0062, written by compute_kpis only on
  // rows where both sides of the pair are known.
  //
  // The numerator is NOT total_stops: a work order that names no route
  // contributes no route-day, so it is out of both sides (normalize.py,
  // _route_day_population). total_stops keeps counting every stop.
  stops_per_route_per_day: {
    table: "kpi_summary",
    num: "stops_route_matched",
    denom: "route_day_count",
  },
  // Both columns are NULL outside front_load / compactor and NULL on any row
  // the labor-coverage gate suppresses, so this sum can neither blend a
  // roll-off row in nor re-derive a rate the gate refused. Best Disposal has no
  // usable labor-time feed on front load (14.2% of its August central_coast
  // work orders carry a positive labor_hours), so every one of its rows is
  // suppressed and the tile says it awaits source data rather than printing
  // 53.44 lifts an hour. Delta's rows all clear the gate and read 7.69 on the
  // sum basis for August 2026 against a 10.39 average of rates.
  lifts_per_hour: {
    table: "kpi_summary",
    num: "lift_count_labor_matched",
    denom: "labor_hours_lift_matched",
  },
  // MER-647: revenue per cubic yard, Seth Heath's replacement for Rev / Lift.
  // Both halves are written only on a front_load or roll_off row that carries
  // earned revenue AND a positive container-yards total (migration 0081), so
  // an earned-only month or a row with no recorded container size drops out of
  // both sums. Read one line at a time: a front-load yard and a roll-off yard
  // are priced far apart, so the tiles pin equipment_type.
  rev_per_yard: {
    table: "kpi_summary",
    num: "earned_revenue_yard_matched",
    denom: "yards_revenue_matched",
  },
  fuel_pct_of_revenue: {
    table: "financial_health",
    num: "fuel_cost_total",
    denom: "total_invoiced",
  },
  maintenance_pct_of_revenue: {
    table: "financial_health",
    num: "maintenance_cost_total",
    denom: "total_invoiced",
  },
  // MER-324: ARPC (Average Revenue per Customer) on Executive Roll-Up
  // tab. Industry-standard formula is canonical: SUM(earned_revenue)
  // over the filter window divided by COUNT(DISTINCT active_customer)
  // over the same window. The pipeline materializes both numerator
  // (earned_revenue, MER-149) and denominator (active_customers,
  // MER-324) on kpi_summary at the same earned-period grain, and
  // the SUM/SUM ratio here is the unbiased aggregate (mirrors
  // MER-320's reasoning: AVG of per-period ratios over-weights
  // low-volume periods).
  arpc: {
    table: "kpi_summary",
    num: "earned_revenue",
    denom: "active_customers",
  },
  // MER-370: dollar-weighted company-wide delinquency rate. The
  // pipeline emits one snapshot row per division on financial_health
  // (Jackson, Hattiesburg, Dallas for DWS). delinquency_rate_pct is
  // already dollar-weighted per division (MER-318), but
  // `aggregation: latest` then picks ONE arbitrary division's row
  // (period_start tiebreak is non-deterministic when all snapshots
  // share the same YYYY-MM), leaving Billing & Collections + the
  // Exec roll-up showing Dallas 0.0% one paint and Hattiesburg 11.2%
  // the next. SUM(ar_over_60_total) / SUM(ar_total) collapses the
  // per-division rows into the unbiased company rate. Monthly rows
  // carry NULL for both columns so they drop out of the sum (PostgREST
  // SUM ignores NULL), and snapshots are REPLACE-mode per
  // refresh, so there's no historical accumulation to time-window.
  delinquency_rate_pct: {
    table: "financial_health",
    num: "ar_over_60_total",
    denom: "ar_total",
  },
  // MER-464: Collection Rate on Billing & Collections (L1b.4) + the Exec
  // roll-up (L6a.1a) read financial_health.collection_rate_pct with
  // `aggregation: latest`, the same arbitrary-division trap as MER-370:
  // one monthly row per division shares period_start, so LIMIT 1 picks a
  // non-deterministic division. A single division-month rate >100% is
  // business-normal (collections lag billing across month boundaries:
  // Hattiesburg 2026-05 collected $264.9K against $181.1K invoiced =
  // 146.3%), which trips the MER-449 +/-100% review band and rendered
  // "Not available, under review" even after the MER-458 source fix
  // landed. SUM(cash_collected) / SUM(total_invoiced) collapses the
  // per-division monthly rows into the dollar-weighted company rate over
  // the loaded window (DWS 92.7%, BD 89.4% on the 2026-06-09 prod probe).
  // Snapshot rows carry NULL for both columns and drop out of the SUM.
  collection_rate_pct: {
    table: "financial_health",
    num: "cash_collected",
    denom: "total_invoiced",
  },
  // MER-386: RO / FL / ASL avg CM% tiles (L4.1b-ro, L4.1b-fl, L4.1b-asl)
  // read route_kpis.gross_margin_pct with aggregation: avg, which is
  // AVG-of-ratios. A roll-off route with revenue = 0 but booked disposal
  // cost yields a large negative per-route ratio, and the unweighted
  // average then produced the -238.6% RO headline. The unbiased,
  // industry-standard contribution margin is revenue-weighted:
  // SUM(gross_margin) / SUM(total_revenue). Both dollar building blocks
  // are populated on route_kpis (normalize.compute_route_kpis). Verified
  // on the prod pooler 2026-06-02: RO AVG-of-ratios -238.6% becomes
  // SUM/SUM 59.8%; FL 91.9% becomes 80.0%; ASL stays 100.0%. The
  // company-wide gross_margin_pct tile on kpi_summary is intentionally
  // left on the avg path (the table guard keys this entry to route_kpis).
  gross_margin_pct: {
    table: "route_kpis",
    num: "gross_margin",
    denom: "total_revenue",
  },
  // Seth 2026-09-02 ("some of the metrics are not calculating at all"),
  // sibling of the disposal_coverage_pct finding below. Widget 1.1.4 on
  // templates/growth.yaml declared `aggregation: sum` on this stored
  // fraction, which adds recovery ratios across division rows and lands
  // past the +/-100% review band on any month with more than one
  // division billing disposal. Unlike disposal_coverage_pct, this one
  // HAS both building blocks on kpi_summary: migration 0018 defines the
  // stored column as disposal_fee_charged / total_disposal_cost, so the
  // unbiased window aggregate is the SUM/SUM of the same two dollar
  // columns. Same asymmetry the disposal_burden_pct entry already
  // carries: a period beyond the disposal horizon (MER-500) holds a NULL
  // total_disposal_cost that PostgREST drops from the denominator while
  // any disposal_fee_charged on that row still counts, so recovery reads
  // slightly high until the disposal feed catches up.
  disposal_fee_recovery_pct: {
    table: "kpi_summary",
    num: "disposal_fee_charged",
    denom: "total_disposal_cost",
  },
  // Wave 1 Disposal tab (AJ, 24 Sep 2026): the tab reads public.disposal at
  // ticket grain, where the sync stamps the pipeline's unit rule on every
  // ticket (preprocessing/disposal_tracker.py). $/ton is the priced-by-weight
  // pair, the same pair kpi_summary.disposal_cost_per_ton divides, summed over
  // the tickets on screen before dividing (MER-715). COVERAGE_FLOORS below
  // withholds it under the MER-720 floor. Named so neither can shadow a
  // stored column.
  disposal_ticket_cost_per_ton: {
    table: "disposal",
    num: "cost_ton_priced",
    denom: "tons_priced",
  },
  // Wave 1 Disposal tab: the fee-over-cost recovery ratio, the same two
  // columns as disposal_fee_recovery_pct but under its own name so the tab's
  // tile can treat a zero fee against real cost as a missing fee feed
  // (ZERO_NUMERATOR_IS_MISSING) without changing the existing tiles.
  disposal_recovery_fee_over_cost: {
    table: "kpi_summary",
    num: "disposal_fee_charged",
    denom: "total_disposal_cost",
  },
  // MER-717 / MER-705: the Meridian Board tab (templates/professional.yaml,
  // tab "Board"). Each key is a board catalogue ratio, named `board_*` so it
  // can never shadow a stored kpi_summary column, and each side is an
  // additive input column from migration 0059 or earlier. Revenue basis is
  // EARNED (the board design's recommendation, an AJ decision): the stored
  // disposal_burden_pct above divides by work-order revenue instead, and
  // rev_per_haul / miss_rate_pct are stored per-row ratios the catalogue
  // refuses to bind. Same MER-500 caveat as disposal_burden_pct: a month
  // beyond the disposal horizon holds a NULL cost that drops out of the
  // numerator only, so the disposal share reads low until that feed lands.
  board_disposal_cost_pct_revenue: {
    table: "kpi_summary",
    num: "total_disposal_cost",
    denom: "earned_revenue",
  },
  // Both sides are written only on rows where the earned revenue AND the
  // service's own unit count are known, so revenue never divides units from
  // a period it does not share. Read one service at a time.
  board_revenue_per_service_unit: {
    table: "kpi_summary",
    num: "earned_revenue_unit_matched",
    denom: "service_unit_count_revenue_matched",
  },
  // Not serviced over attempted (completed plus not serviced), so a period
  // with nothing attempted is empty rather than a perfect 0%.
  board_service_miss_rate: {
    table: "kpi_summary",
    num: "not_serviced_count",
    denom: "attempted_wo_count",
  },
  // Change against the same month a year earlier over that month's revenue.
  // normalize.py writes both sides for every row of a month or for none, so
  // a month without a comparable prior year drops out of both sums.
  board_revenue_growth_yoy: {
    table: "kpi_summary",
    num: "earned_revenue_yoy_change",
    denom: "earned_revenue_prior_year",
  },
};

/**
 * Seth 2026-09-02, "some of the metrics are not calculating at all".
 *
 * A stored ratio column whose NUMERATOR was never materialized, so the
 * SUM(num) / SUM(denom) registry above cannot express it. kpi_summary's
 * disposal_coverage_pct is computed in normalize.py as
 * `count(tipping_fee_charged > 0) / wo_count` per (period, equipment
 * type, division) group; only the ratio and its denominator (`wo_count`)
 * are persisted, the numerator count is discarded at write time.
 *
 * Widget 1.1.4 declared `aggregation: sum` on it, which ADDS fractions
 * across division rows. Best Disposal August read 0.819 (main) + 0.400
 * (central_coast) = 1.219, past the +/-100% review band, so the tile
 * rendered "Not available, under review"; July's 0.812 + 0.000 = 0.812
 * stayed in band and rendered a number. Intermittent by month, which is
 * exactly why it reads to a customer as "stopped working".
 *
 * `avg` would render every month but is wrong in a new way: it weights a
 * 5-work-order division exactly like a 1,345-work-order one. Verified
 * read-only against prod 2026-09-02, Best Disposal roll_off August:
 * sum 1.2190 (blank), avg 0.6095 (61.0%, RED under this tile's <80
 * band), volume-weighted 0.8174 (81.7%, YELLOW). SUM(ratio * weight) /
 * SUM(weight) is the identity that recovers the correct aggregate from a
 * stored ratio plus its own denominator, and `wo_count` IS that
 * denominator, stored and populated on every row. No new column, no
 * re-ingest.
 *
 * DWS is the louder case: three divisions sum to ~2.3-2.4 every month,
 * so this tile has been blank on the DWS Operations tab since it
 * shipped, not just intermittently. Weighted reads 0.817 for August.
 *
 * PostgREST cannot multiply two columns inside an aggregate select, so
 * this path reads the matching rows and folds them client side. Only
 * period-grain tables (kpi_summary and friends) may be registered here;
 * see WEIGHTED_RATIO_ROW_CAP.
 *
 * Chart series and tabular fills still route through DERIVED_RATIOS
 * only. No manifest trends or tabulates disposal_coverage_pct today; a
 * future one needs the same fold taught to those paths.
 */
export const WEIGHTED_RATIOS: Record<string, { table: string; weight: string }> = {
  disposal_coverage_pct: { table: "kpi_summary", weight: "wo_count" },
};

/**
 * MER-684 / MER-715: stored ratios whose additive components are NOT stored,
 * so no SUM(numerator) / SUM(denominator) can be built for them.
 *
 * normalize.py computes yards_per_haul as haul yards over the count of hauls
 * that recorded a volume (MER-664), and hours_per_haul as labor hours over the
 * count of hauls that recorded labor (MER-421; MER-700 moves the hours onto
 * those same hauls, which changes the numerator but not what is missing here).
 * Both counts, and the haul-only yards and hours, are discarded at write time.
 * The stored haul_count is every haul, and total_yards / total_labor_hours
 * cover deliveries as well, so dividing those would be a different population,
 * and weighting the stored ratio by haul_count is only approximate.
 * These tiles averaged the stored ratios across divisions and weeks, which is
 * the defect the ratio contract removes.
 *
 * A single matching row is an honest reading of the stored ratio. More than one
 * is refused with UNCOMBINABLE_COPY rather than combined. `missing` names the
 * components the pipeline would have to persist before an entry can move to
 * DERIVED_RATIOS; it is documentation for that follow-up, not a column list
 * any query reads.
 */
export const UNCOMBINABLE_RATIOS: Record<string, { table: string; missing: string[] }> = {
  yards_per_haul: { table: "kpi_summary", missing: ["haul_yards", "yard_haul_count"] },
  hours_per_haul: { table: "kpi_summary", missing: ["haul_labor_hours", "labor_haul_count"] },
};

/**
 * MER-695: KPI tile columns withheld behind GATE_COPY ("awaiting source data")
 * whatever the rows hold, because every value the query could return today is
 * built from inputs known to be incomplete. The tiles stay on the page (a
 * missing tile hides the gap; MER-483 kept connector-gated tiles as Pending
 * for the same reason), and no aggregation runs.
 *
 *   cost_per_mile. Once MER-655 lands it is the row's own disposal cost over
 *   the row's own miles, and no source attributes miles to a division and line
 *   of business yet. Before that it divided card-only fuel and maintenance by
 *   company miles. Its honest registration afterwards is SUM(disposal cost) /
 *   SUM(miles) over only the rows whose stored ratio is not null (a row with
 *   partial miles is nulled there but keeps both components), a population
 *   restriction DERIVED_RATIOS cannot express yet.
 *
 *   fuel_cost_per_mile. Card-only fuel over company miles, the same company
 *   figure stamped on every (equipment, division) row.
 *
 *   fuel_pct_of_revenue, maintenance_pct_of_revenue. Card-only costs, and the
 *   company Ramp total is stamped on every division row of financial_health,
 *   so the DERIVED_RATIOS SUM(cost) / SUM(total_invoiced) path counts the cost
 *   once per division. Lifting the withhold before the cost is stored once per
 *   period brings that multiple back.
 *
 * Remove an entry only when its input is fixed at the source. The ratio guard
 * (scripts/ratio-contract.test.mjs) then requires the column to be read through
 * a registered SUM/SUM path, so a withhold cannot quietly fall back to `avg`.
 * Tiles only: a chart, pane or table reading one of these columns is not
 * withheld here, and the guard refuses those shapes.
 */
export const WITHHELD_RATIOS: Record<string, { table: string; until: string }> = {
  cost_per_mile: {
    table: "kpi_summary",
    until: "miles recorded per division and line of business",
  },
  fuel_cost_per_mile: {
    table: "kpi_summary",
    until: "general ledger fuel cost, stored once per period",
  },
  fuel_pct_of_revenue: {
    table: "financial_health",
    until: "general ledger fuel cost, stored once per period instead of on every division row",
  },
  maintenance_pct_of_revenue: {
    table: "financial_health",
    until:
      "general ledger maintenance cost, stored once per period instead of on every division row",
  },
};

/** The withheld tile result for `col` on `table`, or null when it is not withheld. */
export function withheldKpi(col: string, table: string): KpiResult | null {
  const entry = WITHHELD_RATIOS[col];
  if (!entry || entry.table !== table) return null;
  return { kind: "kpi", scalar: null, isEmpty: false, gateCopy: GATE_COPY };
}

/**
 * MER-695: DERIVED_RATIOS cost shares whose numerator reads zero only when its
 * cost feed carried nothing for the window (normalize.py writes 0.0 for a month
 * with no categorized spend). A hauler does not have a month with no
 * maintenance or no fuel; a 0.0% here is a missing feed rendered as a perfect
 * (green) result. With revenue present and the cost at zero, the tile withholds
 * the number behind GATE_COPY instead. Both columns are in WITHHELD_RATIOS
 * today, which answers first; this is the gate that must still hold when that
 * withhold lifts.
 */
export const ZERO_NUMERATOR_IS_MISSING = new Set([
  "fuel_pct_of_revenue",
  "maintenance_pct_of_revenue",
  // Wave 1 Disposal tab: no disposal fee billed against real disposal cost is
  // a fee feed that did not land, not a 0% recovery.
  "disposal_recovery_fee_over_cost",
]);

/**
 * MER-720: ratios computed over a SUBSET of the rows the tile is filtered to,
 * and the share of the whole that subset has to reach before the answer is
 * allowed to describe the selection.
 *
 * Disposal $/ton divides ton-priced cost by ton-priced tons. That is the only
 * honest way to state a rate per ton, but Delta tips more than half its volume
 * at facilities that bill by the cubic yard, so a filtered month can leave a
 * couple of weighed tickets carrying a figure for a month of yard-billed
 * spend. Hattiesburg roll-off published $46.20 per ton off 0.10% of that
 * month's disposal cost, and $90.00 per ton off 0.81%. Both were real ticket
 * prices and neither described the month.
 *
 * `floor` is set from what the number CLAIMS, never from observed values
 * (MER-716): a per-ton figure asserts a cost per ton for the grain on screen,
 * so a simple majority of that grain's spend has to be priced per ton. It
 * mirrors DISPOSAL_TON_COVERAGE_FLOOR in preprocessing/normalize.py, which
 * applies the same rule to the stored column; this is the read-side half,
 * because the tile re-derives the ratio over whatever the client selected and
 * the pipeline cannot know that selection in advance.
 *
 * Renders GATE_COPY, not a zero, and the tile loses its drill affordance.
 */
export const COVERAGE_FLOORS: Record<string, { of: string; floor: number }> = {
  disposal_cost_per_ton: { of: "total_disposal_cost", floor: 0.5 },
  // Wave 1 Disposal tab: the same rule at ticket grain. cost_resolved is the
  // ticket's share of total_disposal_cost.
  disposal_ticket_cost_per_ton: { of: "cost_resolved", floor: 0.5 },
};

/**
 * MER-467: figures built only from hauls that came back with a scale ticket,
 * and the coverage statement that has to travel with them.
 *
 * Every roll-off swap, removal and spot haul terminates at a disposal
 * facility, so each one should produce a ticket. On the 2026-09-21 Delta
 * warehouse 1,872 of 11,600 did not. Those hauls contribute nothing to
 * total_disposal_cost while still counting in haul_count and in stops, so
 * every cost below reads LOW and every margin built on it reads HIGH. The
 * audit's disposal_review finding and `meridian admin disposal-gap-review`
 * carry the actual work orders so ops can go and find them.
 *
 * This registry does not hide any of it. Hiding a figure tells a reader
 * nothing and takes away a number they were already using; stating the
 * coverage beside it tells them exactly how much to trust it.
 *
 * `num` / `of` are summed over the SAME rows, filters and window as the
 * value, in the same round trip, so the share describes the client's actual
 * selection rather than some period the pipeline guessed at (MER-715: sum
 * over sum, never an average of rates).
 *
 * The coverage columns are written for roll-off rows only and are NULL
 * everywhere else, so a front-load or residential tile produces no note at
 * all. That is deliberate. A front-load route dump is one ticket for an
 * entire route and carries no work order number, so front-load work orders
 * without a ticket are the normal case; running them through this rule would
 * publish front load as ~0% covered, which is false.
 */
export const COVERAGE_NOTES: Record<
  string,
  { table: string; num: string; of: string; unit: string }
> = Object.fromEntries(
  [
    // The disposal cost itself: a sum of the tickets that exist.
    "total_disposal_cost",
    // Cost ratios whose numerator is ticketed-only while the denominator
    // counts every haul or stop, so they are understated by the shortfall.
    "disposal_cost_per_haul",
    "disposal_cost_per_stop",
    "disposal_burden_pct",
    "disposal_cost_pct_of_revenue",
    // Revenue minus an understated cost, so this one reads HIGH.
    "gross_margin_per_stop",
    // A haul with no cost can never test below cost, so this can only
    // undercount.
    "total_disposal_loss",
  ].map((column) => [
    column,
    {
      table: "kpi_summary",
      num: "disposal_ticketed_haul_count",
      of: "disposal_ticket_population",
      unit: "hauls",
    },
  ]),
);

/**
 * "Based on 84% of hauls (1,297 of 1,398 had a disposal ticket)".
 *
 * Returns null at full coverage: when every haul produced a ticket there is
 * nothing to caveat, so the note disappears on its own the day the source gap
 * closes rather than needing a follow-up PR to remove it. Also null when the
 * population is absent or zero, which is how a non-roll-off tile stays silent.
 */
export function coverageNoteFor(
  ticketed: number | null,
  population: number | null,
  unit: string,
): string | null {
  if (population === null || !Number.isFinite(population) || population <= 0) return null;
  if (ticketed === null || !Number.isFinite(ticketed)) return null;
  if (ticketed >= population) return null;
  const pct = Math.round((ticketed / population) * 100);
  return (
    `Based on ${pct}% of ${unit} ` +
    `(${ticketed.toLocaleString()} of ${population.toLocaleString()} had a disposal ticket)`
  );
}

/**
 * MER-467: attach the ticket-coverage statement to a resolved tile.
 *
 * Additive only. It never changes the scalar, never sets gateCopy and never
 * touches isEmpty, so a figure that renders today renders identically with a
 * sentence of provenance beside it. A withheld or empty tile gets no note:
 * there is no number for the coverage to describe.
 */
function withCoverageNote(
  result: KpiResult,
  note: { unit: string } | undefined,
  ticketed: number | null,
  population: number | null,
): KpiResult {
  if (!note || result.scalar === null || result.gateCopy) return result;
  const copy = coverageNoteFor(ticketed, population, note.unit);
  return copy ? { ...result, coverageNote: copy } : result;
}

/** Map a fold outcome onto the KPI tile contract (MER-317 isEmpty semantics). */
function kpiFromFold(folded: FoldResult): KpiResult {
  switch (folded.state) {
    case "value":
      return { kind: "kpi", scalar: folded.value, isEmpty: false };
    case "uncombinable":
      return { kind: "kpi", scalar: null, isEmpty: false, gateCopy: UNCOMBINABLE_COPY };
    case "missing":
      return { kind: "kpi", scalar: null, isEmpty: false, gateCopy: GATE_COPY };
    default:
      return { kind: "kpi", scalar: null, isEmpty: true };
  }
}

/**
 * Row cap for the weighted fold. kpi_summary at (period x equipment type
 * x division x period_type) grain returns single digits of rows under
 * any widget's filters, so this never fires in practice; it exists so a
 * mis-registered entry against a large table cannot silently fold a
 * truncated page into a biased average. On truncation the tile refuses
 * rather than publishing a number we cannot stand behind.
 */
const WEIGHTED_RATIO_ROW_CAP = 2000;

// MER-545: when priorBounds is set (a "vs prior" second pass), the widget's
// own timeline window is replaced with the explicit prior-window bounds on
// the same timeline column. Everything else about the query (filters,
// tenant scope, now-cutoff) is identical, so current and prior measure the
// same thing over adjacent windows.
function applyWindowOrTimeline(
  query: SupabaseFilter,
  widget: ManifestWidget,
  priorBounds: { gte: string; lt: string } | null,
): SupabaseFilter {
  if (priorBounds && widget.timeline?.column) {
    const col = canonicalColumnName(widget.timeline.column);
    return query.gte(col, priorBounds.gte).lt(col, priorBounds.lt);
  }
  return applyTimeline(query, widget);
}

/**
 * Wave 1 release (AJ 2026-09-28): the tabs whose empty single-number tiles
 * are checked for an unfilled source column before they say "Limited
 * activity". Scoped to the Disposal tab, where the live check found tiles
 * blaming the date range for columns this tenant has never filled.
 */
const UNFILLED_COLUMN_TABS = new Set(["Disposal"]);

const filledColumnCache = new Map<string, Promise<boolean>>();

/** Test seam: the fill cache is module-level. */
export function __resetFilledColumnCache(): void {
  filledColumnCache.clear();
}

/**
 * Whether this tenant has at least one non-null, non-zero value in `col`,
 * over all time and ignoring the tile's filters. One limit-1 read per
 * (tenant, table, column). A zero counts as unfilled: a fee or cost column
 * that has only ever held 0 is a feed that never arrived, not a measurement.
 */
async function columnEverFilled(table: string, col: string, tenantId: string): Promise<boolean> {
  const key = `${tenantId}::${table}::${col}`;
  let pending = filledColumnCache.get(key);
  if (!pending) {
    pending = (async () => {
      const supabase = getSupabase();
      const q: SupabaseFilter = scopeTenantTable(supabase.from(table).select(col), tenantId, table)
        .not(col, "is", null)
        .neq(col, 0)
        .limit(1);
      const { data, error } = await q;
      if (error) throw new Error((error as { message?: string }).message ?? "Supabase error");
      return Array.isArray(data) && data.length > 0;
    })();
    filledColumnCache.set(key, pending);
    pending.catch(() => {
      if (filledColumnCache.get(key) === pending) filledColumnCache.delete(key);
    });
  }
  return pending;
}

/**
 * The stored columns a tile's number is built from: the numerator and
 * denominator of a DERIVED_RATIOS entry, else the bound column itself.
 */
function backingColumns(col: string, table: string): string[] {
  const derived = DERIVED_RATIOS[col];
  if (derived && derived.table === table) return [derived.num, derived.denom];
  return [col];
}

/**
 * An empty single-number tile on an UNFILLED_COLUMN_TABS tab: when any column
 * behind the number has never been filled for this tenant, the honest copy is
 * the awaiting-source-data gate, not "Limited activity this period. Try a
 * wider range." (a range that will never fill). A tile whose columns do carry
 * values elsewhere keeps the empty-range copy: that period really is empty.
 * A failed probe leaves the result alone.
 */
async function gateUnfilledColumns(
  widget: ManifestWidget,
  tenantId: string,
  result: KpiResult,
): Promise<KpiResult> {
  if (!result.isEmpty || result.gateCopy || result.scalar !== null) return result;
  if (!widget.tab || !UNFILLED_COLUMN_TABS.has(widget.tab)) return result;
  if (!widget.source || !widget.field || isKpiComparisonField(widget.field)) return result;
  if (!widget.field.column) return result;
  const table = resolveSourceName(widget.source);
  const col = columnNameFor(widget.field.column, widget.source);
  try {
    for (const c of backingColumns(col, table)) {
      if (!(await columnEverFilled(table, c, tenantId))) {
        return { kind: "kpi", scalar: null, isEmpty: false, gateCopy: GATE_COPY };
      }
    }
  } catch {
    return result;
  }
  return result;
}

async function executeKpiQuery(
  widget: ManifestWidget,
  tenantId: string,
  priorBounds: { gte: string; lt: string } | null = null,
): Promise<KpiResult> {
  const result = await executeKpiQueryInner(widget, tenantId, priorBounds);
  // A prior-window pass only feeds the delta; it never picks the tile's copy.
  return priorBounds ? result : gateUnfilledColumns(widget, tenantId, result);
}

async function executeKpiQueryInner(
  widget: ManifestWidget,
  tenantId: string,
  priorBounds: { gte: string; lt: string } | null = null,
): Promise<KpiResult> {
  // No source, no field, or a kpi_comparison-shaped field means there's no
  // single-column query to run. kpi_comparison widgets run their own multi-
  // pane queries inside KpiComparisonTile.
  if (!widget.source || !widget.field || isKpiComparisonField(widget.field)) {
    return { kind: "kpi", scalar: null };
  }
  // Captured before the rewrite below: resolveLatestFilters returns a new
  // widget object, which discards the narrowing the guard above just proved.
  // It only ever rewrites `filter`, so source and field are carried across.
  const source = widget.source;
  const field = widget.field;
  // MER-695: a withheld column never reaches an aggregation (WITHHELD_RATIOS).
  if (field.column) {
    const withheld = withheldKpi(columnNameFor(field.column, source), resolveSourceName(source));
    if (withheld) return withheld;
  }
  // MER-589: rewrite any {op:"latest"} pin to a literal before the query builds.
  widget = await resolveLatestFilters(widget, tenantId);
  if (!field.column) {
    // Defensive: prevents `select=undefined.sum()` URLs (MER-133).
    return { kind: "kpi", scalar: null };
  }
  const supabase = getSupabase();
  const agg = (field.aggregation ?? "sum") as string;
  // MER-309: use the source-aware resolver so CQT views (PascalCase aliases
  // like "Row_Count") keep their casing. Canonical tables still lowercase.
  const col = columnNameFor(field.column, source);
  const table = resolveSourceName(source);

  // MER-150: completion_pct is a derived percentage, not a stored column.
  // Per-row equivalent is the MER-78 is_completed boolean (0/1); avg yields
  // the rate in [0,1]. MER-176: scale to percent units (0-100) here so this
  // path matches the canonical convention used by every other `_pct` column
  // (migrations/0008 generated columns + normalize.py aggregates both store
  // 0-100). The format layer just appends `%`.
  if (col === "completion_pct" && table === "service_event") {
    let q: SupabaseFilter = scopeTenantTable(
      supabase.from(table).select(`is_completed.avg()`),
      tenantId,
      table,
    );
    q = applyFilters(q, widget);
    q = applyWindowOrTimeline(q, widget, priorBounds);
    q = applyNowCutoff(q, widget, table);
    const { data, error } = await q;
    if (error) throw new Error((error as { message?: string }).message ?? "Supabase error");
    if (!Array.isArray(data) || data.length === 0) {
      // MER-317: no result row means PostgREST saw zero source rows.
      return { kind: "kpi", scalar: null, isEmpty: true };
    }
    const row = data[0] as Record<string, unknown>;
    // MER-184: completion_pct is now a fraction; format.ts multiplies for the
    // glyph. Drop the previous * 100 scaling that paired with MER-176.
    const rawAvg = row.avg ?? row.is_completed;
    const fraction = toScalar(rawAvg);
    // MER-317: a null avg means no service events landed in the range.
    // A real avg of 0 means rows exist but none were completed, which
    // is still a valid measurement and should render as 0%.
    const isEmpty = rawAvg === null || rawAvg === undefined;
    return { kind: "kpi", scalar: fraction, isEmpty };
  }

  // DERIVED_RATIOS is module-scope (MER-483); see the registry above.
  const derived = DERIVED_RATIOS[col];
  if (derived && table === derived.table) {
    // MER-720: the coverage aggregate rides along in the SAME round trip and
    // under the SAME filters, so the share is measured over exactly the rows
    // the client selected.
    const coverage = COVERAGE_FLOORS[col];
    // MER-467: the ticket-coverage counts ride the same round trip and the
    // same filters, so the share describes exactly the rows on screen.
    const note = COVERAGE_NOTES[col];
    const noteExpr =
      note && note.table === table ? `,covnum:${note.num}.sum(),covden:${note.of}.sum()` : "";
    const selectExpr = `num:${derived.num}.sum(),denom:${derived.denom}.sum()`;
    let q: SupabaseFilter = scopeTenantTable(
      supabase
        .from(table)
        .select((coverage ? `${selectExpr},cov:${coverage.of}.sum()` : selectExpr) + noteExpr),
      tenantId,
      table,
    );
    q = applyFilters(q, widget);
    q = applyWindowOrTimeline(q, widget, priorBounds);
    q = applyNowCutoff(q, widget, table);
    q = applyFeedQuality(q, table, "derived_ratio");
    const { data, error } = await q;
    if (error) throw new Error((error as { message?: string }).message ?? "Supabase error");
    if (!Array.isArray(data) || data.length === 0) {
      // MER-317: no result row -> PostgREST saw zero source rows.
      return { kind: "kpi", scalar: null, isEmpty: true };
    }
    const row = data[0] as Record<string, unknown>;
    // MER-317 semantics: numerator/denominator null means the underlying
    // sum returned null because no source rows matched. denominator = 0
    // with rows present is a defined-but-undefined ratio (0/0), still
    // empty for display purposes. A numerator of 0 with denominator > 0
    // is a real measurement (Adjustment Rate = 0%); scalar = 0 with
    // isEmpty = false renders the literal "0%" tile. MER-695: except for
    // the cost shares in ZERO_NUMERATOR_IS_MISSING, where a zero cost over
    // real revenue is a missing feed and renders the gate, not a green 0.0%.
    const folded = kpiFromFold(
      foldDerivedRatio(toScalar(row.num), toScalar(row.denom), {
        zeroNumeratorIsMissing: ZERO_NUMERATOR_IS_MISSING.has(col),
        coverage: coverage ? toScalar(row.num) : null,
        coverageOf: coverage ? toScalar(row.cov) : null,
        coverageFloor: coverage ? coverage.floor : null,
      }),
    );
    return withCoverageNote(folded, note, toScalar(row.covnum), toScalar(row.covden));
  }

  // Seth 2026-09-02: a stored ratio whose numerator was never persisted,
  // folded client side as SUM(ratio * weight) / SUM(weight) over the same
  // filters / window / now-cutoff every other tile uses. See the
  // WEIGHTED_RATIOS registry above for why sum and avg are both wrong.
  const weighted = WEIGHTED_RATIOS[col];
  if (weighted && table === weighted.table) {
    let q: SupabaseFilter = scopeTenantTable(
      supabase
        .from(table)
        .select(`${col},${weighted.weight}`)
        .limit(WEIGHTED_RATIO_ROW_CAP + 1),
      tenantId,
      table,
    );
    q = applyFilters(q, widget);
    q = applyWindowOrTimeline(q, widget, priorBounds);
    q = applyNowCutoff(q, widget, table);
    q = applyFeedQuality(q, table, "weighted_ratio");
    const { data, error } = await q;
    if (error) throw new Error((error as { message?: string }).message ?? "Supabase error");
    if (!Array.isArray(data) || data.length === 0) {
      // Same MER-317 semantics as the derived branch: no rows in range.
      return { kind: "kpi", scalar: null, isEmpty: true };
    }
    if (data.length > WEIGHTED_RATIO_ROW_CAP) {
      // Truncated page: the fold would be biased toward whichever rows
      // PostgREST happened to return. Refuse rather than guess.
      return { kind: "kpi", scalar: null, isEmpty: true };
    }
    let weightedSum = 0;
    let weightTotal = 0;
    for (const r of data as Record<string, unknown>[]) {
      const ratio = toScalar(r[col]);
      const rowWeight = toScalar(r[weighted.weight]);
      // A null ratio means the slice was never measured, and a zero or
      // absent weight carries no information. Both drop out of the fold
      // instead of being read as a real zero rate.
      if (ratio === null || rowWeight === null || rowWeight <= 0) continue;
      weightedSum += ratio * rowWeight;
      weightTotal += rowWeight;
    }
    // Every candidate row was unmeasured or unweighted: nothing to anchor
    // the ratio, so the tile stays empty rather than reading 0%.
    if (weightTotal === 0) return { kind: "kpi", scalar: null, isEmpty: true };
    return { kind: "kpi", scalar: weightedSum / weightTotal, isEmpty: false };
  }

  // MER-684: a stored ratio with no stored components. Read at most two
  // matching rows: one is the honest stored value, two means the selection
  // spans rows the dashboard cannot combine (UNCOMBINABLE_RATIOS above).
  const uncombinable = UNCOMBINABLE_RATIOS[col];
  if (uncombinable && table === uncombinable.table) {
    // A prior window is a second combined read of the same kind; no delta.
    if (priorBounds) return { kind: "kpi", scalar: null, isEmpty: true };
    let q: SupabaseFilter = scopeTenantTable(
      supabase.from(table).select(col).not(col, "is", null).limit(2),
      tenantId,
      table,
    );
    q = applyFilters(q, widget);
    q = applyTimeline(q, widget);
    q = applyNowCutoff(q, widget, table);
    q = applyFeedQuality(q, table, "single_row_ratio");
    const { data, error } = await q;
    if (error) throw new Error((error as { message?: string }).message ?? "Supabase error");
    return kpiFromFold(foldSingleRow((data ?? []) as Record<string, unknown>[], col));
  }

  // MER-560: net_count = count(add-overlay rows) minus count(subtract-overlay
  // rows). Each side merges its overlay onto the widget's shared filter and
  // runs through the identical window / now-cutoff machinery, so the two
  // counts measure the same period as the sibling delivery/removal tiles.
  // Reconciliation with those tiles is enforced by construction:
  // overview-tab.test.mjs asserts the overlays equal the siblings' filters
  // verbatim. Count cannot be null on a successful query, so "no activity
  // on either side" (0 - 0) is the empty case; a real zero net from equal
  // nonzero flows renders as the literal 0.
  if (agg === "net_count" || agg === "ratio_count") {
    // Shared by net_count and ratio_count (MER-560): count the widget's
    // rows with an extra filter overlay merged onto the base filter, through
    // the identical window / now-cutoff machinery as any sibling tile.
    // MER-630: `extra` carries denominator clauses the numerator overlay
    // shadows. A filter object is a shallow merge, so a numerator key that
    // names the same column as a denominator key would REPLACE it and the
    // numerator would leave the denominator's rows. Passing those clauses
    // through a second applyFilters pass ANDs them instead (PostgREST
    // accumulates predicates), so the intersection is what gets counted.
    const overlayCount = async (
      overlay: Record<string, unknown> | undefined,
      extra?: Record<string, unknown>,
    ): Promise<number | null> => {
      const sideWidget = {
        ...widget,
        filter: { ...(widget.filter ?? {}), ...(overlay ?? {}) },
      } as ManifestWidget;
      let q: SupabaseFilter = scopeTenantTable(
        supabase.from(table).select(`${col}.count()`),
        tenantId,
        table,
      );
      q = applyFilters(q, sideWidget);
      if (extra && Object.keys(extra).length > 0) {
        q = applyFilters(q, { ...widget, filter: extra } as ManifestWidget);
      }
      q = applyWindowOrTimeline(q, widget, priorBounds);
      q = applyNowCutoff(q, widget, table);
      const { data, error } = await q;
      if (error) throw new Error((error as { message?: string }).message ?? "Supabase error");
      if (!Array.isArray(data) || data.length === 0) return null;
      const row = data[0] as Record<string, unknown>;
      return toScalar(row.count ?? row[col]);
    };
    if (agg === "net_count") {
      const added = (await overlayCount(widget.net?.add)) ?? 0;
      const removed = (await overlayCount(widget.net?.subtract)) ?? 0;
      return { kind: "kpi", scalar: added - removed, isEmpty: added === 0 && removed === 0 };
    }
    // ratio_count: fraction of denominator rows matched by the numerator
    // overlay (MER-560 part 2, Swap % of Hauls). A zero/absent denominator
    // is the empty case (0/0 is not a rate); a real 0 numerator over a
    // nonzero denominator is a true 0% measurement.
    //
    // MER-630: THE NUMERATOR IS COUNTED INSIDE THE DENOMINATOR'S POPULATION.
    // Each side used to merge only its own overlay onto the shared filter, so
    // the two sides counted two different sets and the "fraction" was a
    // fraction of nothing in particular. Best Disposal's Swap % of Hauls
    // counted 1,367 swap work orders over 10,796 rows flagged is_haul, and 115
    // of those swaps (48 HL Swap, 67 Trailer Swap) carry is_haul = false: they
    // were in the numerator and not in the denominator, on a tile whose name
    // says "of Hauls". Composing the overlays fixes the whole class, not the
    // instance, and it cannot drift: a numerator row that fails a denominator
    // clause is simply not counted.
    //
    // Whether a hook-lift or tractor-trailer swap SHOULD be a haul is a live
    // question (MER-617, Chris's haul definition). This is not that answer.
    // Changing the haul flag changes the denominator and this ratio follows it
    // automatically, because the numerator is now defined in terms of it.
    const denOverlay = (widget.ratio?.denominator ?? {}) as Record<string, unknown>;
    const numOverlay = (widget.ratio?.numerator ?? {}) as Record<string, unknown>;
    const shadowed: Record<string, unknown> = {};
    for (const [key, clause] of Object.entries(denOverlay)) {
      if (key in numOverlay) shadowed[key] = clause;
    }
    const num = (await overlayCount({ ...denOverlay, ...numOverlay }, shadowed)) ?? 0;
    const den = (await overlayCount(denOverlay)) ?? 0;
    if (den === 0) {
      return { kind: "kpi", scalar: null, isEmpty: true };
    }
    return { kind: "kpi", scalar: num / den, isEmpty: false };
  }

  // PostgREST 11+ aggregate column expression: `column.agg()`. NOT `agg(column)`
  // (which PostgREST reads as an embedded-relationship spec, not an aggregate).
  if (POSTGREST_AGGS.has(agg)) {
    // MER-467: same round trip, same filters, same window as the value.
    const plainNote = COVERAGE_NOTES[col];
    const plainNoteExpr =
      plainNote && plainNote.table === table
        ? `,covnum:${plainNote.num}.sum(),covden:${plainNote.of}.sum()`
        : "";
    let q: SupabaseFilter = scopeTenantTable(
      supabase.from(table).select(`${col}.${agg}()` + plainNoteExpr),
      tenantId,
      table,
    );
    q = applyFilters(q, widget);
    q = applyWindowOrTimeline(q, widget, priorBounds);
    q = applyNowCutoff(q, widget, table);
    q = applyFeedQuality(q, table, agg);
    const { data, error } = await q;
    if (error) throw new Error((error as { message?: string }).message ?? "Supabase error");
    if (!Array.isArray(data) || data.length === 0) {
      return { kind: "kpi", scalar: null, isEmpty: true };
    }
    const row = data[0] as Record<string, unknown>;
    const rawValue = row[agg] ?? row[col];
    // MER-309: date-aware max/min. event_date is a DATE column, so
    // PostgREST returns an ISO string like "2026-05-30". toScalar
    // coerces that to NaN and L3d.2b Data Freshness renders n/a even
    // though the value is present. When the widget declares
    // `display_format: days` (the Internal DQ Data Freshness tile) and
    // the aggregation is min/max, pass the raw string through; the
    // rendering layer already handles string scalars per MER-306 close.
    if (
      (agg === "max" || agg === "min") &&
      (widget.display_format === "days" || widget.display_format === "date")
    ) {
      return {
        kind: "kpi",
        scalar: rawValue == null ? null : String(rawValue),
        // MER-317 + MER-309: a null max/min means PostgREST saw no rows
        // (PostgREST returns one result row with the aggregate set to
        // null when the source set is empty), so this is the empty case.
        isEmpty: rawValue === null || rawValue === undefined,
      };
    }
    const scalar = toScalar(rawValue);
    // MER-317: distinguish "no rows in range" from "valid zero" so the
    // KPI tile can render the limited-activity copy instead of bare n/a.
    //   sum / avg / min / max: PostgREST returns null when no rows
    //   match. 0 with rows present is a real measurement (Adjustment
    //   Rate = 0%, zero-fee day, etc.).
    //   count: PostgREST returns 0 when no rows match; that IS the
    //   empty case because count cannot be null on a successful query.
    // MER-465 PR B: a widget may declare count_zero_is_data when a zero
    // count is a real, desirable measurement (L4.3c "Routes LOW Count" =
    // 0 means no low-margin routes), not an empty range to mute.
    const isEmpty =
      agg === "count"
        ? scalar === null || (scalar === 0 && widget.count_zero_is_data !== true)
        : rawValue === null || rawValue === undefined;
    return withCoverageNote(
      { kind: "kpi", scalar, isEmpty },
      plainNote,
      toScalar(row.covnum),
      toScalar(row.covden),
    );
  }

  // `latest`: most recent row by the timeline column (falling back to
  // period_start), return its column value. Timeline filters still apply
  // so "latest in last 30 days" works.
  //
  // MER-297 follow-up: skip rows where the requested column is NULL.
  // financial_health emits multiple row shapes per (period, division):
  // a snapshot row carrying point-in-time fields (DSO, unapplied_cash,
  // delinquency_rate_pct, etc.) and monthly rows carrying period
  // aggregates (collection_rate_pct, cash_collected). With MER-280
  // partial-row scaffolding the running calendar month also emits an
  // empty monthly row with collection_rate_pct = NULL. Picking strictly
  // the highest period_start surfaced that empty row and rendered n/a
  // on Collection Rate. "Latest value of X" implicitly means "latest
  // row where X has a value"; rows that do not carry the column are
  // not candidates.
  if (agg === "latest") {
    // MER-545: "latest" is an as-of read; a prior-window re-run would just
    // surface an older row, which is not a comparable baseline. No delta.
    if (priorBounds) {
      return { kind: "kpi", scalar: null, isEmpty: true };
    }
    const orderCol = canonicalColumnName(widget.timeline?.column ?? "period_start");
    let q: SupabaseFilter = scopeTenantTable(
      supabase.from(table).select(`${col}, ${orderCol}`),
      tenantId,
      table,
    )
      .not(col, "is", null)
      .order(orderCol, { ascending: false })
      .limit(1);
    q = applyFilters(q, widget);
    q = applyTimeline(q, widget);
    q = applyNowCutoff(q, widget, table);
    const { data, error } = await q;
    if (error) throw new Error((error as { message?: string }).message ?? "Supabase error");
    if (!Array.isArray(data) || data.length === 0) {
      // MER-317: latest already excludes rows where the target column is
      // null and orders by the timeline column, so an empty result means
      // no row in the requested range carries a value for this KPI.
      return { kind: "kpi", scalar: null, isEmpty: true };
    }
    // MER-306 close: when the widget declares display_format: text, pass the
    // raw value through. toScalar would coerce a text categorical
    // (route_margin_trend_direction = "improving" / "stable" / "worsening")
    // to NaN and the tile would render n/a even though the column is
    // populated. Numeric latest reads (delta in dollars, etc.) keep the
    // existing toScalar path.
    const rawValue = (data[0] as Record<string, unknown>)[col];
    if (widget.display_format === "text" || widget.display_format === "string") {
      return {
        kind: "kpi",
        scalar: rawValue == null ? null : String(rawValue),
        isEmpty: rawValue == null,
      };
    }
    return { kind: "kpi", scalar: toScalar(rawValue), isEmpty: rawValue == null };
  }

  // `distinct_count`: PostgREST has no native COUNT(DISTINCT). Fetch the column
  // and dedupe client-side. Acceptable for KPI Summary / Customers (low-cardinality);
  // would need a view if we ever apply this to SERVICE_EVENT.
  if (agg === "distinct_count") {
    // MER-545: distinct_count fetches rows client-side; doubling that cost
    // for a delta is not worth it. No prior support.
    if (priorBounds) {
      return { kind: "kpi", scalar: null, isEmpty: true };
    }
    let q: SupabaseFilter = scopeTenantTable(supabase.from(table).select(col), tenantId, table);
    q = applyFilters(q, widget);
    q = applyTimeline(q, widget);
    q = applyNowCutoff(q, widget, table);
    const { data, error } = await q;
    if (error) throw new Error((error as { message?: string }).message ?? "Supabase error");
    if (!Array.isArray(data)) return { kind: "kpi", scalar: 0, isEmpty: true };
    const seen = new Set<unknown>();
    for (const r of data as Record<string, unknown>[]) {
      const v = r[col];
      if (v !== null && v !== undefined) seen.add(v);
    }
    // MER-317: empty unique set means no rows in range carried a value.
    // MER-697: honour count_zero_is_data here too. It only existed on the
    // `count` branch above, so moving L4.3c "Routes LOW Count" onto
    // distinct_count would have brought back exactly the MER-465 defect the
    // flag was added to stop: a month with no low-margin routes is the best
    // possible reading, and it must render 0, not "awaiting source data".
    return {
      kind: "kpi",
      scalar: seen.size,
      isEmpty: seen.size === 0 && widget.count_zero_is_data !== true,
    };
  }

  throw new Error(`Unsupported aggregation '${agg}' for column '${col}' on '${table}'`);
}

/**
 * Wave 1 Disposal tab: an opt-in cap on a rows read. A ticket-grain source
 * holds thousands of rows per month and PostgREST silently truncates an
 * uncapped read at its max-rows setting, so a ticket list declares
 * `row_limit` (and `order_by`) and gets exactly that many rows, newest first,
 * plus the true match count so the table can say what it left out. A pivot
 * over the same source aggregates server side (MeasurePivotTable) and
 * declares `row_limit: 1`, which turns its rows read into an emptiness probe
 * instead of a 1,000-row download it never uses. Widgets without `row_limit`
 * are untouched.
 */
export function rowLimitOf(widget: ManifestWidget): number | null {
  const raw = (widget as ManifestWidget & { row_limit?: unknown }).row_limit;
  return typeof raw === "number" && Number.isInteger(raw) && raw > 0 ? raw : null;
}

function applyRowLimit(q: SupabaseFilter, widget: ManifestWidget, limit: number): SupabaseFilter {
  const order = (widget as ManifestWidget & { order_by?: { column?: string; direction?: string } })
    .order_by;
  let out = q;
  if (order?.column) {
    out = out.order(canonicalColumnName(order.column), {
      ascending: order.direction === "asc",
      nullsFirst: false,
    });
  }
  return out.limit(limit);
}

async function executeRowsQuery(widget: ManifestWidget, tenantId: string): Promise<RowsResult> {
  if (!widget.source) return { kind: "rows", rows: [] };
  const source = widget.source;
  // MER-589: see executeKpiQuery. Tables and charts pin the same way.
  widget = await resolveLatestFilters(widget, tenantId);
  const supabase = getSupabase();
  const table = resolveSourceName(source);
  const limit = rowLimitOf(widget);
  let q: SupabaseFilter = scopeTenantTable(
    limit === null
      ? supabase.from(table).select("*")
      : supabase.from(table).select("*", { count: "exact" }),
    tenantId,
    table,
  );
  q = applyFilters(q, widget);
  q = applyTimeline(q, widget);
  q = applyNowCutoff(q, widget, table);
  if (limit !== null) q = applyRowLimit(q, widget, limit);

  const { data, error, count } = await q;
  if (error) throw new Error((error as { message?: string }).message ?? "Supabase error");
  const rows = (data ?? []) as Record<string, unknown>[];
  if (limit !== null) {
    // A capped read never takes the MER-470 fallback below: it is a list of
    // the newest rows in the window, and it says how many it left out.
    return { kind: "rows", rows, totalCount: typeof count === "number" ? count : rows.length };
  }

  // MER-470: latest-populated-period fallback. The DWS SE feed's newest
  // event was June 1 while Driver Performance pointed at "this week"
  // (June 8-14): zero rows was the truth, but rendering "No data for this
  // period" while the source holds rows one week back is an empty section
  // the operator cannot act on. Probe the newest timeline value (filters +
  // now-cutoff still applied), rebuild the widget's declared window around
  // it, and return those rows WITH the label so the period shift is
  // explicit. Applies to period-mode tables only.
  // Table shapes only (charts/trends are excluded: re-anchoring a trend
  // axis would mislabel a time series, while a table of "the most recent
  // rows, labeled as such" is exactly what an operator wants during lag).
  const shape = effectiveChartType(widget);
  // MER-692: never under an explicit calendar month. The user named the
  // period; answering with a different one, labelled or not, overrides that
  // choice. (computeFallbackWindow has no branch for a month token either;
  // this guard keeps it that way if one is ever added.)
  if (
    rows.length === 0 &&
    (shape === "tabular" || shape === "pivot") &&
    widget.timeline?.mode === "period" &&
    widget.timeline.column &&
    !parseMonthToken(widget.timeline.range)
  ) {
    const col = canonicalColumnName(widget.timeline.column);
    let probe: SupabaseFilter = scopeTenantTable(supabase.from(table).select(col), tenantId, table)
      .not(col, "is", null)
      .order(col, { ascending: false })
      .limit(1);
    probe = applyFilters(probe, widget);
    probe = applyNowCutoff(probe, widget, table);
    const { data: maxRows } = await probe;
    const maxVal =
      Array.isArray(maxRows) && maxRows.length > 0
        ? (maxRows[0] as Record<string, unknown>)[col]
        : null;
    if (maxVal != null) {
      const win = computeFallbackWindow(
        widget.timeline.range,
        String(maxVal),
        TIMELINE_DAY_RANGES[widget.timeline.range ?? ""],
      );
      if (win) {
        let fq: SupabaseFilter = scopeTenantTable(
          supabase.from(table).select("*"),
          tenantId,
          table,
        );
        fq = applyFilters(fq, widget);
        fq = fq.gte(col, win.gte).lt(col, win.lt);
        fq = applyNowCutoff(fq, widget, table);
        const { data: fbData, error: fbError } = await fq;
        if (!fbError && Array.isArray(fbData) && fbData.length > 0) {
          return {
            kind: "rows",
            rows: fbData as Record<string, unknown>[],
            fallbackPeriodLabel: win.label,
          };
        }
      }
    }
  }

  return { kind: "rows", rows };
}

/**
 * Test seam (MER-715): the single-number dispatch, so node:test can run a
 * manifest widget through the real registry branches against the PostgREST
 * stand-in instead of asserting on this file's source text.
 */
export const __executeKpiQueryForTest = executeKpiQuery;

/**
 * Compile and execute the widget's data fetch. Returns a state object
 * suitable for direct destructuring in React components.
 *
 * MER-156: when the widget's manifest status is anything other than
 * "ok" (blocked / deferred / suppressed), skip the fetch entirely.
 * The renderer already short-circuits to the BLOCKED_STATUS_CAPTION
 * placeholder, but pre-fix the hook still fired the underlying
 * Supabase query each page load, producing a 400 Bad Request for
 * widgets whose source columns don't exist yet (e.g. MER-135
 * overage_capture_rate). DWS manifest has ~29 such widgets so the
 * waste was meaningful.
 */
export function useWidgetQuery(widget: ManifestWidget): QueryState {
  // AR Aging panels (arAgingData.ts), RO Efficiency panels
  // (roEfficiencyData.ts) and Revenue tab views (revenueTabData.ts) read their
  // own data, so the generic query is skipped exactly as for a blocked widget.
  const isBlocked =
    (Boolean(widget.status) && widget.status !== "ok") ||
    effectiveChartType(widget) === "ar_aging" ||
    effectiveChartType(widget) === "ro_efficiency" ||
    effectiveChartType(widget) === "revenue_tab";
  const tenantId = useTenantId();
  const [state, setState] = useState<QueryState>({
    data: null,
    isLoading: !isBlocked,
    error: null,
  });

  useEffect(() => {
    if (isBlocked) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }
    // Fail-closed: hold in the loading state until the slug resolves to a
    // tenant_id. Firing before then would build an unscoped query.
    if (!tenantId) {
      setState({ data: null, isLoading: true, error: null });
      return;
    }

    let cancelled = false;
    const chartType = effectiveChartType(widget);
    const boundTenantId = tenantId;

    async function run() {
      try {
        const isKpi = chartType === "single_number" || chartType === undefined;
        const data = isKpi
          ? await executeKpiQuery(widget, boundTenantId)
          : await executeRowsQuery(widget, boundTenantId);
        // MER-545: `trend: vs_prior_period` runs the same aggregate over the
        // adjacent prior window (priorWindow.mjs bounds). Demo mode skips it
        // (MER-294 forces YTD, so "prior" would be meaningless), and a prior
        // failure never fails the tile; the delta just stays absent.
        //
        // Refused outright for period-bucketed tables: kpi_summary /
        // route_kpis / financial_health rows anchor period_date to the
        // bucket START (monthly rows on the 1st, weekly rows on Monday), so
        // a trailing window holds the partial in-progress bucket while the
        // prior window holds complete buckets. That renders a large fake
        // negative delta on flat volume, the exact A3 partial-vs-full trap
        // from Delta's fix plan. Trend is only honest on event-grain
        // sources; period-grain deltas need pipeline-computed prior columns
        // (MER-553). Snapshot-subtype widgets are refused for the same
        // family of reasons: applyTimeline never windows them, so current
        // would be all-history vs a windowed prior.
        //
        // MER-692: under an explicit calendar month the prior is the previous
        // calendar month with the same bounds applyTimeline gives the current
        // one on these event-grain sources (priorWindowBounds). The
        // period-grain refusal above is what keeps that exact: a weekly snap
        // on kpi_summary could not be mirrored by a plain calendar prior, so
        // those tables never get here. An incomplete month has no prior.
        const trendable =
          widget.trend === "vs_prior_period" &&
          widget.kpi_subtype !== "snapshot" &&
          (!widget.source || !PERIOD_GRAIN_TABLES.has(resolveSourceName(widget.source)));
        if (isKpi && data.kind === "kpi" && trendable && !isDemoMode()) {
          const bounds = priorWindowBounds(widget.timeline, new Date());
          if (bounds) {
            try {
              const prior = await executeKpiQuery(widget, boundTenantId, bounds);
              data.prior =
                prior.kind === "kpi" && typeof prior.scalar === "number" ? prior.scalar : null;
            } catch {
              data.prior = null;
            }
          }
        }
        if (!cancelled) setState({ data, isLoading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        setState({
          data: null,
          isLoading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      }
    }

    setState({ data: null, isLoading: true, error: null });
    void run();

    return () => {
      cancelled = true;
    };
  }, [widget, isBlocked, tenantId]);

  return state;
}

/**
 * MER-222: the row-level drill's read plan for one widget.
 *
 * The drill is a promise that the rows on screen are the rows behind the
 * number, so it has to reproduce the tile's read: the same projection scope,
 * and the same FEED_INCOMPLETE decision. Both answers depend on registries
 * that live in this module (DERIVED_RATIOS, WEIGHTED_RATIOS,
 * UNCOMBINABLE_RATIOS), so the resolution lives here and the column algebra
 * lives in drillQuery.mjs where node:test can reach it.
 *
 * `select` never names an internal column, so the plumbing the presentation
 * layer drops is not sent at all. `feedShape` is the same string
 * executeKpiQuery hands applyFeedQuality for this widget, chosen by the same
 * branch order: derived ratio, then weighted ratio, then uncombinable ratio,
 * then the declared aggregation. A tile that SUMS a starved period keeps it
 * and so does its drill; a tile that averages drops it and so does its drill.
 */
export function drillReadPlan(
  widget: ManifestWidget,
  table: string,
): { select: string; feedShape: string } {
  const field = widget.field;
  const col =
    field && !isKpiComparisonField(field) && field.column
      ? columnNameFor(field.column, widget.source)
      : null;

  const derived = col ? DERIVED_RATIOS[col] : undefined;
  if (derived && derived.table === table) {
    return {
      select: drillSelect(widget, table, [derived.num, derived.denom]),
      feedShape: "derived_ratio",
    };
  }
  const weighted = col ? WEIGHTED_RATIOS[col] : undefined;
  if (weighted && weighted.table === table) {
    return { select: drillSelect(widget, table, [weighted.weight]), feedShape: "weighted_ratio" };
  }
  const uncombinable = col ? UNCOMBINABLE_RATIOS[col] : undefined;
  if (uncombinable && uncombinable.table === table) {
    return { select: drillSelect(widget, table), feedShape: "single_row_ratio" };
  }
  const agg =
    field && !isKpiComparisonField(field) && typeof field.aggregation === "string"
      ? field.aggregation
      : "sum";
  return { select: drillSelect(widget, table), feedShape: agg };
}
